import { Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { ChatAttachment, ChatService } from '../stokker/api/chat.service';

/** A chatben 3D-ben megnézhető fájlok (Boris küldi, vagy bárki feltölti). */
export const MODEL_EXTENSIONS = ['stl', '3mf', 'obj'];

export function isModelFile(name: string | null | undefined): boolean {
  const ext = `${name || ''}`.split('.').pop()?.toLowerCase() || '';
  return MODEL_EXTENSIONS.includes(ext);
}

/**
 * Forgatható 3D-előnézet (STL / 3MF / OBJ) teljes képernyőn. A three.js csak megnyitáskor töltődik be,
 * így a chat többi részét nem lassítja. Egér / ujj: forgatás, görgő / csípés: nagyítás.
 */
@Component({
  selector: 'app-chat-model-viewer',
  template: `
    <div class="mv" (click)="close.emit()">
      <div class="mv__bar" (click)="$event.stopPropagation()">
        <span class="mv__name">{{ attachment.name }}<small *ngIf="dims"> · {{ dims }}</small></span>
        <button type="button" class="mv__btn" (click)="resetView()" title="Alapnézet" aria-label="Alapnézet">⟲</button>
        <button type="button" class="mv__btn" (click)="download.emit(attachment)" title="Letöltés" aria-label="Letöltés">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 4 v11"></path><polyline points="7,10 12,15 17,10"></polyline><path d="M5 19 h14"></path></svg>
        </button>
        <button type="button" class="mv__btn" (click)="close.emit()" aria-label="Bezárás">✕</button>
      </div>
      <div class="mv__stage" #stage (click)="$event.stopPropagation()">
        <div class="mv__msg" *ngIf="state !== 'ready'">{{ state === 'loading' ? 'A modell betöltése…' : error }}</div>
      </div>
      <div class="mv__hint" *ngIf="state === 'ready'">Forgatás: húzd · Nagyítás: görgő / két ujj</div>
    </div>
  `,
  styles: [`
    .mv { position: fixed; inset: 0; z-index: 1000; background: rgba(12, 14, 16, 0.92); display: flex; flex-direction: column; }
    .mv__bar { display: flex; align-items: center; gap: 6px; padding: 10px 12px; color: #fff; }
    .mv__name { flex: 1; min-width: 0; font-size: 14px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .mv__name small { opacity: 0.7; }
    .mv__btn { width: 38px; height: 38px; border: 0; border-radius: 10px; background: rgba(255,255,255,0.08); color: #fff; font-size: 17px; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; }
    .mv__btn:hover { background: rgba(255,255,255,0.16); }
    .mv__stage { position: relative; flex: 1; min-height: 0; margin: 0 12px; border-radius: 14px; overflow: hidden; background: radial-gradient(circle at 50% 35%, #3A4048 0%, #1B1E22 75%); touch-action: none; }
    .mv__msg { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; color: #D6DADF; font-size: 14px; text-align: center; padding: 20px; }
    .mv__hint { color: rgba(255,255,255,0.6); font-size: 12px; text-align: center; padding: 8px 0 12px; }
  `],
})
export class ChatModelViewerComponent implements OnInit, OnDestroy {
  @Input({ required: true }) attachment!: ChatAttachment;
  @Output() close = new EventEmitter<void>();
  @Output() download = new EventEmitter<ChatAttachment>();
  @ViewChild('stage', { static: true }) stage!: ElementRef<HTMLDivElement>;

  state: 'loading' | 'ready' | 'error' = 'loading';
  error = '';
  dims = '';

  private disposed = false;
  private cleanup: (() => void) | null = null;
  private reset: (() => void) | null = null;

  constructor(private http: HttpClient, private chat: ChatService, private zone: NgZone) {}

  ngOnInit(): void {
    this.render().catch((e) => {
      this.state = 'error';
      this.error = `A modellt nem sikerült megnyitni. (${e?.message || e})`;
    });
  }

  ngOnDestroy(): void {
    this.disposed = true;
    this.cleanup?.();
  }

  resetView(): void {
    this.reset?.();
  }

  private async render(): Promise<void> {
    const [THREE, { STLLoader }, { ThreeMFLoader }, { OBJLoader }, { OrbitControls }] = await Promise.all([
      import('three'),
      import('three/examples/jsm/loaders/STLLoader.js'),
      import('three/examples/jsm/loaders/3MFLoader.js'),
      import('three/examples/jsm/loaders/OBJLoader.js'),
      import('three/examples/jsm/controls/OrbitControls.js'),
    ]);
    const buffer = await firstValueFrom(this.http.get(this.chat.attachmentPath(this.attachment), { responseType: 'arraybuffer' }));
    if (this.disposed) return;

    const ext = this.attachment.name.split('.').pop()?.toLowerCase();
    const material = new THREE.MeshStandardMaterial({ color: 0xc9d1d9, roughness: 0.55, metalness: 0.05 });
    let object: import('three').Object3D;
    if (ext === 'stl') {
      const geometry = new STLLoader().parse(buffer);
      geometry.computeVertexNormals();
      object = new THREE.Mesh(geometry, material);
    } else if (ext === '3mf') {
      object = new ThreeMFLoader().parse(buffer);
      // a 3MF saját színeit megtartjuk, ha vannak; különben a semleges anyag
      object.traverse((child: any) => {
        if (child.isMesh && (!child.material || child.material.color?.getHex?.() === 0xffffff)) child.material = material;
      });
    } else {
      object = new OBJLoader().parse(new TextDecoder().decode(buffer));
      object.traverse((child: any) => {
        if (child.isMesh) child.material = material;
      });
    }
    // a nyomtatók Z-felfelé dolgoznak, a three.js Y-felfelé
    object.rotation.x = -Math.PI / 2;

    const host = this.stage.nativeElement;
    const scene = new THREE.Scene();
    const box = new THREE.Box3().setFromObject(object);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    object.position.sub(center);
    object.position.y += size.y / 2;
    const radius = Math.max(size.x, size.y, size.z) || 1;
    this.dims = `${size.x.toFixed(1)} × ${size.z.toFixed(1)} × ${size.y.toFixed(1)} mm`;
    scene.add(object);

    const grid = new THREE.GridHelper(radius * 2.4, 24, 0x6b7380, 0x3a4048);
    scene.add(grid);
    scene.add(new THREE.HemisphereLight(0xffffff, 0x30343a, 1.6));
    const key = new THREE.DirectionalLight(0xffffff, 1.6);
    key.position.set(radius, radius * 2, radius * 1.5);
    scene.add(key);
    const rim = new THREE.DirectionalLight(0xc6f24e, 0.5);
    rim.position.set(-radius * 1.5, radius, -radius);
    scene.add(rim);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    host.appendChild(renderer.domElement);
    const camera = new THREE.PerspectiveCamera(40, 1, radius / 100, radius * 50);
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.target.set(0, size.y / 2, 0);

    this.reset = () => {
      camera.position.set(radius * 1.3, radius * 1.0, radius * 1.6);
      controls.target.set(0, size.y / 2, 0);
      controls.update();
    };
    this.reset();

    const resize = () => {
      const w = host.clientWidth || 1;
      const h = host.clientHeight || 1;
      renderer.setSize(w, h, false);
      renderer.domElement.style.width = '100%';
      renderer.domElement.style.height = '100%';
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(host);

    let frame = 0;
    this.zone.runOutsideAngular(() => {
      const loop = () => {
        frame = requestAnimationFrame(loop);
        controls.update();
        renderer.render(scene, camera);
      };
      loop();
    });
    this.state = 'ready';

    this.cleanup = () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      controls.dispose();
      renderer.dispose();
      scene.traverse((child: any) => {
        child.geometry?.dispose?.();
      });
      material.dispose();
      renderer.domElement.remove();
    };
  }
}
